/**
 * Checkout screen — self-contained order processing UI for a single patient.
 *
 * Fetches orders, categorises them, and delegates to self-contained sections
 * (pending preparation, cart/checkout). The caller decides whether to mount
 * this inside a Dialog, a page, or a story layout.
 */
import { ExternalLink, PartyPopper, User } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { APPOINTMENT_TYPES_BY_ID, matchesPredicate, ORDER_PREDICATES } from '@snootsvet/shared'

import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { StripeNotLinkedBadge } from '@/frontend/features/billing/stripe-not-linked-badge'
import { CheckoutCartSection } from '@/frontend/features/orders/checkout-screen/checkout-cart-section'
import { CheckoutOutOfStockSection } from '@/frontend/features/orders/checkout-screen/checkout-out-of-stock-section'
import { CheckoutPendingSection } from '@/frontend/features/orders/checkout-screen/checkout-pending-section'
import { RecentOrdersTable } from '@/frontend/features/orders/checkout-screen/recent-orders-table'
import { useListPatientOrders } from '@/frontend/features/orders/hooks/use-list-patient-orders'

export interface CheckoutScreenProps {
  /** Patient whose orders to display and process. */
  patientId: string
  /** Display name override — falls back to the first order's patient name. */
  patientName?: string
}

/** Checkout screen — fetches patient orders and renders the checkout UI. */
export function CheckoutScreen({ patientId, patientName: patientNameProp }: CheckoutScreenProps) {
  const { data: orders, isLoading } = useListPatientOrders(patientId, {
    include: 'stock',
    refetchInterval: 10_000,
  })

  const patientName = patientNameProp ?? orders?.[0]?.patient.name
  const appointment = orders?.[0]?.consultation?.appointment
  // Owning member — surfaced below the title so staff can confirm who they're charging.
  const owner = orders?.[0]?.patient.owner

  const checkoutOrders = useMemo(
    () => (orders ?? []).filter((o) => o.status !== 'returned' && o.status !== 'cancelled'),
    [orders]
  )

  const appointmentSubtitle = appointment ? (
    <>
      <FormattedDateTime value={appointment.startTime} clinicId={appointment.clinicId} />
      {appointment.appointmentTypeId && APPOINTMENT_TYPES_BY_ID[appointment.appointmentTypeId] && (
        <> · {APPOINTMENT_TYPES_BY_ID[appointment.appointmentTypeId]?.name}</>
      )}
    </>
  ) : null

  // ---- Mutation lock — prevents concurrent actions across sections ----

  const [mutating, setMutating] = useState(false)

  // ---- Order categorisation ----

  const pendingOrders = useMemo(
    () => checkoutOrders.filter((o) => o.status === 'pending'),
    [checkoutOrders]
  )

  const cartOrders = useMemo(
    () =>
      checkoutOrders.filter(
        (o) => o.status === 'prepared' || matchesPredicate(o, ORDER_PREDICATES.invoiceable)
      ),
    [checkoutOrders]
  )

  const outOfStockOrders = useMemo(
    () => checkoutOrders.filter((o) => o.status === 'out_of_stock'),
    [checkoutOrders]
  )

  const recentOrders = useMemo(() => {
    const cutoff = Date.now() - 365 * 24 * 60 * 60 * 1000
    return checkoutOrders.filter(
      (o) =>
        matchesPredicate(o, ORDER_PREDICATES.fulfilled) && new Date(o.createdAt).getTime() >= cutoff
    )
  }, [checkoutOrders])

  const hasActiveOrders =
    pendingOrders.length > 0 || cartOrders.length > 0 || outOfStockOrders.length > 0

  return (
    <div>
      {/* Patient header — name, owner details, and appointment info inline on one row. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
        <h2 className="text-headline-md">
          <Link to={`/patients/${patientId}`} className="hover:underline">
            {patientName ?? 'Patient'}
          </Link>
        </h2>
        {owner && (
          <>
            <span className="text-muted-foreground/50">|</span>
            <Link
              to={`/members/${owner.id}`}
              className="inline-flex items-center gap-1 font-semibold text-foreground hover:underline"
            >
              <User className="size-3 shrink-0" />
              {owner.firstName} {owner.lastName}
            </Link>
            {owner.stripeDashboardUrl ? (
              // Mirrors the "View in Stripe" link on member-page's account summary card.
              <a
                href={owner.stripeDashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                View in Stripe
                <ExternalLink className="size-3" />
              </a>
            ) : (
              <StripeNotLinkedBadge />
            )}
          </>
        )}
        {appointmentSubtitle && (
          <>
            <span className="text-muted-foreground/50">|</span>
            <span>{appointmentSubtitle}</span>
          </>
        )}
      </div>

      {isLoading ? (
        <ScreenSkeleton />
      ) : hasActiveOrders ? (
        <div className="flex flex-col gap-4">
          {pendingOrders.length > 0 && (
            <CheckoutPendingSection
              patientId={patientId}
              orders={pendingOrders}
              mutating={mutating}
              onMutatingChange={setMutating}
            />
          )}

          {cartOrders.length > 0 && (
            <CheckoutCartSection
              cartOrders={cartOrders}
              patientId={patientId}
              mutating={mutating}
              onMutatingChange={setMutating}
            />
          )}

          <CheckoutOutOfStockSection orders={outOfStockOrders} />
          <RecentOrdersTable orders={recentOrders} />
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 py-12">
          <PartyPopper className="h-10 w-10 text-muted-foreground" />
          <p className="text-muted-foreground">Nothing to do — all orders are settled!</p>
          <RecentOrdersTable orders={recentOrders} />
        </div>
      )}
    </div>
  )
}

function ScreenSkeleton() {
  return (
    <div className="space-y-3 py-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  )
}
